import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, AlertCircle, Folder, ChevronRight, ArrowLeft, Search, FolderPlus, Trash2, CheckCircle } from 'lucide-react';
import { archiveService } from '../services/archive.service';
import { commissionService } from '../services/commission.service';
import { folderService, archiveFolderService } from '../services/folder.service';
import { viewedItemsService } from '../services/viewedItems.service';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import SearchBar from '../components/SearchBar';
import DocumentCard from '../components/DocumentCard';
import WindowsFolder from '../components/WindowsFolder';
import ConfirmModal from '../components/ConfirmModal';

const DocumentsList = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  const [archives, setArchives] = useState([]);
  const [filteredArchives, setFilteredArchives] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [viewMode, setViewMode] = useState('folders'); // 'folders' or 'search'
  const [hoveredFolderId, setHoveredFolderId] = useState(null);
  const [viewedCommissions, setViewedCommissions] = useState([]);
  const [viewedFolders, setViewedFolders] = useState([]);

  // États pour les sous-dossiers
  const [subFolders, setSubFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [draggedArchive, setDraggedArchive] = useState(null);

  // États pour le drag & drop mobile
  const [touchStartPos, setTouchStartPos] = useState(null);
  const [isDraggingMobile, setIsDraggingMobile] = useState(false);
  const [dragPreview, setDragPreview] = useState({ x: 0, y: 0, visible: false });
  const [dropSuccess, setDropSuccess] = useState(false);

  // États pour la gestion des erreurs
  const [errorModal, setErrorModal] = useState({
    show: false,
    title: '',
    message: '',
  });

  useEffect(() => {
    fetchData();
    fetchViewedItems();
  }, []);

  const fetchViewedItems = async () => {
    try {
      const [commissions, folders] = await Promise.all([
        viewedItemsService.getViewedCommissions(),
        viewedItemsService.getViewedFolders(),
      ]);
      setViewedCommissions(commissions || []);
      setViewedFolders(folders || []);
    } catch (error) {
      console.error('Erreur lors du chargement des éléments vus:', error);
      // En cas d'erreur, continuer avec des tableaux vides
    }
  };

  // Cleanup: restaurer le scroll si le composant est démonté pendant un drag
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const [archivesData, commissionsData] = await Promise.all([
        archiveService.getArchives({
          page: 0,
          size: 100,
          sort: 'createdAt,desc',
        }),
        commissionService.getActiveCommissions(),
      ]);

      const archivesList = archivesData.content || [];
      setArchives(archivesList);
      setFilteredArchives(archivesList);
      setCommissions(commissionsData || []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError(error.message || 'Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si l'utilisateur peut gérer les dossiers/archives dans une commission
  const canManageCommission = (commission) => {
    if (!user || !commission) {
      return false;
    }

    // Les administrateurs ont tous les droits
    if (isAdmin()) {
      return true;
    }

    // Vérifier si commission.members existe et est un tableau
    if (!commission.members || !Array.isArray(commission.members)) {
      return false;
    }

    // Vérifier si l'utilisateur est membre de la commission
    // Tester plusieurs structures possibles retournées par le backend
    const isMember = commission.members.some((member) => {
      return member.user?.id === user.id ||
             member.userId === user.id ||
             member.id === user.id;
    });

    return isMember;
  };

  const handleSearch = async (query, searchFilters) => {

    try {
      if (query || Object.keys(searchFilters).length > 0) {
        setViewMode('search');
        // Utiliser la recherche backend si une query existe
        if (query) {
          const searchResults = await archiveService.searchArchives(query, {
            commissionId: searchFilters.commission,
            page: 0,
            size: 100,
          });
          setFilteredArchives(searchResults.results?.content || searchResults.content || []);
        } else {
          // Filtrage local pour les autres filtres
          let filtered = [...archives];

          if (searchFilters.commission) {
            filtered = filtered.filter((archive) =>
              archive.commission?.id?.toString() === searchFilters.commission?.toString()
            );
          }

          if (searchFilters.type) {
            filtered = filtered.filter((archive) => archive.status === searchFilters.type);
          }

          if (searchFilters.year) {
            filtered = filtered.filter((archive) => {
              const archiveYear = archive.archiveDate
                ? new Date(archive.archiveDate).getFullYear().toString()
                : '';
              return archiveYear === searchFilters.year;
            });
          }

          setFilteredArchives(filtered);
        }
      } else {
        // Aucun filtre, revenir à la vue dossiers
        setViewMode('folders');
        setFilteredArchives(archives);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setFilteredArchives([]);
    }
  };

  // Grouper les archives par commission
  const getCommissionsWithArchives = () => {
    const commissionsMap = new Map();

    // Initialiser avec toutes les commissions
    commissions.forEach((commission) => {
      commissionsMap.set(commission.id, {
        ...commission,
        archives: [],
        count: 0,
      });
    });

    // Ajouter les archives à leurs commissions respectives
    archives.forEach((archive) => {
      if (archive.commission) {
        const commission = commissionsMap.get(archive.commission.id);
        if (commission) {
          commission.archives.push(archive);
          commission.count++;
        }
      }
    });

    return Array.from(commissionsMap.values());
  };

  const commissionsWithArchives = getCommissionsWithArchives();

  const handleCommissionClick = async (commission) => {
    setSelectedFolder(null);

    // Charger les détails complets de la commission (avec les membres)
    try {
      const [fullCommissionData, membersData, folders] = await Promise.all([
        commissionService.getCommissionById(commission.id),
        commissionService.getCommissionMembers(commission.id),
        folderService.getFoldersByCommission(commission.id),
      ]);

      // Extraire le tableau de membres depuis l'objet de pagination
      const membersArray = membersData?.content || membersData || [];

      // Combiner les données existantes avec les détails complets et les membres
      const commissionWithDetails = {
        ...commission,
        ...fullCommissionData,
        members: membersArray, // Ajouter le tableau de membres
      };

      setSelectedCommission(commissionWithDetails);
      setSubFolders(folders);
    } catch (error) {
      console.error('Erreur lors du chargement des détails de la commission:', error);
      // Utiliser la commission de base si le chargement échoue
      setSelectedCommission(commission);
      setSubFolders([]);
    }

    // Marquer la commission comme vue
    if (!viewedCommissions.includes(commission.id)) {
      try {
        await viewedItemsService.markCommissionAsViewed(commission.id);
        setViewedCommissions([...viewedCommissions, commission.id]);
      } catch (error) {
        console.error('Erreur lors du marquage de la commission comme vue:', error);
        // Continuer même en cas d'erreur
      }
    }
  };

  const handleBackToFolders = () => {
    setSelectedCommission(null);
    setSelectedFolder(null);
    setViewMode('folders');
  };

  const handleFolderClick = async (folder) => {
    setSelectedFolder(folder);

    // Marquer le sous-dossier comme vu
    if (selectedCommission && !viewedFolders.includes(folder.id)) {
      try {
        await viewedItemsService.markFolderAsViewed(selectedCommission.id, folder.id);
        setViewedFolders([...viewedFolders, folder.id]);
      } catch (error) {
        console.error('Erreur lors du marquage du sous-dossier comme vu:', error);
        // Continuer même en cas d'erreur
      }
    }
  };

  // Vérifier les droits avant d'ouvrir le modal de création de dossier
  const handleCreateFolderClick = () => {
    const hasAccess = canManageCommission(selectedCommission);

    if (!hasAccess) {
      setErrorModal({
        show: true,
        title: 'Accès refusé',
        message: 'Vous n\'avez pas le droit de créer un dossier dans cette commission. Seuls les membres de la commission et les administrateurs peuvent gérer les sous-dossiers.',
      });
      return;
    }

    setShowCreateFolderModal(true);
  };

  // Gestion des sous-dossiers
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const newFolder = await folderService.createFolder(selectedCommission.id, {
        name: newFolderName,
      });
      setSubFolders([...subFolders, newFolder]);
      setNewFolderName('');
      setShowCreateFolderModal(false);
    } catch (error) {
      console.error('Erreur lors de la création du dossier:', error);
      setShowCreateFolderModal(false);
      setErrorModal({
        show: true,
        title: 'Erreur',
        message: error.message || 'Une erreur est survenue lors de la création du dossier.',
      });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!canManageCommission(selectedCommission)) {
      setErrorModal({
        show: true,
        title: 'Accès refusé',
        message: 'Vous n\'avez pas le droit de supprimer un dossier dans cette commission.',
      });
      return;
    }

    try {
      await folderService.deleteFolder(selectedCommission.id, folderId);
      setSubFolders(subFolders.filter(f => f.id !== folderId));

      // Recharger les archives pour refléter les changements
      await fetchData();
    } catch (error) {
      console.error('Erreur lors de la suppression du dossier:', error);
      setErrorModal({
        show: true,
        title: 'Erreur',
        message: error.message || 'Une erreur est survenue lors de la suppression du dossier.',
      });
    }
  };

  // Gestion du drag & drop (Desktop)
  const handleDragStart = (e, archive) => {
    setDraggedArchive(archive);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnFolder = async (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedArchive) {
      if (!canManageCommission(selectedCommission)) {
        setErrorModal({
          show: true,
          title: 'Accès refusé',
          message: 'Vous n\'avez pas le droit d\'organiser les archives dans cette commission.',
        });
        setDraggedArchive(null);
        return;
      }

      try {
        await archiveFolderService.assignArchiveToFolder(
          selectedCommission.id,
          folderId,
          draggedArchive.id
        );
        setDraggedArchive(null);
        // Force un re-render en rechargeant la commission
        await handleCommissionClick(selectedCommission);
      } catch (error) {
        console.error('Erreur lors de l\'assignation de l\'archive au dossier:', error);
        setDraggedArchive(null);
        setErrorModal({
          show: true,
          title: 'Erreur',
          message: error.message || 'Une erreur est survenue lors de l\'assignation de l\'archive.',
        });
      }
    }
  };

  const handleDropOnRoot = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedArchive) {
      if (!canManageCommission(selectedCommission)) {
        setErrorModal({
          show: true,
          title: 'Accès refusé',
          message: 'Vous n\'avez pas le droit d\'organiser les archives dans cette commission.',
        });
        setDraggedArchive(null);
        return;
      }

      try {
        // Trouver le dossier contenant l'archive
        const folder = archiveFolderService.getArchiveFolder(draggedArchive.id, subFolders);

        if (folder) {
          await archiveFolderService.removeArchiveFromFolder(
            selectedCommission.id,
            folder.id,
            draggedArchive.id
          );
        }

        setDraggedArchive(null);
        // Force un re-render
        await handleCommissionClick(selectedCommission);
      } catch (error) {
        console.error('Erreur lors du retrait de l\'archive du dossier:', error);
        setDraggedArchive(null);
        setErrorModal({
          show: true,
          title: 'Erreur',
          message: error.message || 'Une erreur est survenue lors du retrait de l\'archive.',
        });
      }
    }
  };

  // Gestion du drag & drop (Mobile - Touch)
  const handleTouchStart = (e, archive) => {
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setDraggedArchive(archive);
    // Ne pas empêcher le comportement par défaut ici pour permettre la détection du mouvement
  };

  const handleTouchMove = (e) => {
    if (!draggedArchive || !touchStartPos) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.y);

    // Activer le drag seulement après un certain mouvement (éviter les clics accidentels)
    if (deltaX > 5 || deltaY > 5) {
      if (!isDraggingMobile) {
        setIsDraggingMobile(true);

        // Vibration haptic pour confirmer le début du drag
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }

        // Bloquer le scroll du body
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
      }

      // IMPORTANT: Empêcher le scroll pendant le drag
      e.preventDefault();
      e.stopPropagation();

      setDragPreview({
        x: touch.clientX,
        y: touch.clientY,
        visible: true,
      });
    }
  };

  const handleTouchEnd = async (e) => {
    // Restaurer le scroll du body
    document.body.style.overflow = '';
    document.body.style.touchAction = '';

    if (!draggedArchive || !isDraggingMobile) {
      // Reset si c'était juste un tap
      setDraggedArchive(null);
      setTouchStartPos(null);
      setIsDraggingMobile(false);
      setDragPreview({ x: 0, y: 0, visible: false });
      return;
    }

    const touch = e.changedTouches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);

    // Chercher le dossier ou la zone de dépôt
    let targetFolderId = null;
    let isRootDrop = false;

    // Vérifier si on est sur un dossier
    const folderElement = element?.closest('[data-folder-id]');
    if (folderElement) {
      targetFolderId = folderElement.getAttribute('data-folder-id');
    }

    // Vérifier si on est sur la zone "Archives non classées"
    const rootDropZone = element?.closest('[data-root-drop]');
    if (rootDropZone) {
      isRootDrop = true;
    }

    // Effectuer l'action
    if (targetFolderId || isRootDrop) {
      // Vérifier les droits avant d'effectuer l'action
      if (!canManageCommission(selectedCommission)) {
        setErrorModal({
          show: true,
          title: 'Accès refusé',
          message: 'Vous n\'avez pas le droit d\'organiser les archives dans cette commission.',
        });
        // Reset
        setDraggedArchive(null);
        setTouchStartPos(null);
        setIsDraggingMobile(false);
        setDragPreview({ x: 0, y: 0, visible: false });
        return;
      }

      // Vibration de succès
      if (navigator.vibrate) {
        navigator.vibrate([30, 10, 30]);
      }

      try {
        if (targetFolderId) {
          await archiveFolderService.assignArchiveToFolder(
            selectedCommission.id,
            targetFolderId,
            draggedArchive.id
          );
        } else {
          // Trouver le dossier contenant l'archive
          const folder = archiveFolderService.getArchiveFolder(draggedArchive.id, subFolders);
          if (folder) {
            await archiveFolderService.removeArchiveFromFolder(
              selectedCommission.id,
              folder.id,
              draggedArchive.id
            );
          }
        }

        // Afficher la notification de succès
        setDropSuccess(true);
        setTimeout(() => setDropSuccess(false), 2000);

        await handleCommissionClick(selectedCommission);
      } catch (error) {
        console.error('Erreur lors de l\'organisation de l\'archive:', error);
        setErrorModal({
          show: true,
          title: 'Erreur',
          message: error.message || 'Une erreur est survenue lors de l\'organisation de l\'archive.',
        });
      }
    }

    // Reset
    setDraggedArchive(null);
    setTouchStartPos(null);
    setIsDraggingMobile(false);
    setDragPreview({ x: 0, y: 0, visible: false });
  };

  // Organiser les archives par dossier
  const getOrganizedArchives = () => {
    if (!selectedCommission) return { withoutFolder: [], byFolder: {} };

    const commissionArchives = selectedCommission.archives || [];
    const withoutFolder = archiveFolderService.getArchivesWithoutFolder(commissionArchives, subFolders);
    const byFolder = {};

    subFolders.forEach(folder => {
      byFolder[folder.id] = archiveFolderService.getArchivesInFolder(folder.id, subFolders);
    });

    return { withoutFolder, byFolder };
  };

  // Extraire les types de documents (statuts) uniques
  const documentTypes = ['PUBLISHED', 'DRAFT', 'ARCHIVED'];

  // Extraire les années uniques des archives
  const years = [...new Set(
    archives
      .filter((archive) => archive.archiveDate)
      .map((archive) => new Date(archive.archiveDate).getFullYear().toString())
  )].sort((a, b) => b - a);

  // Composant modal d'erreur réutilisable (à afficher dans tous les returns)
  const ErrorModalComponent = () => (
    <ConfirmModal
      isOpen={errorModal.show}
      onClose={() => setErrorModal({ show: false, title: '', message: '' })}
      onConfirm={() => setErrorModal({ show: false, title: '', message: '' })}
      title={errorModal.title}
      message={errorModal.message}
      confirmText="Compris"
      variant="danger"
      cancelText=""
    />
  );

  // État de chargement
  if (loading) {
    return (
      <div>
        <Header title="Archives" />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des archives...</p>
          </div>
        </div>

        {/* Modal d'erreur */}
        <ErrorModalComponent />
      </div>
    );
  }

  // État d'erreur
  if (error) {
    return (
      <div>
        <Header title="Archives" />
        <div className="px-8">
          <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
            <div className="flex items-start gap-3 text-red-800">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1">Erreur de chargement</h3>
                <p className="text-sm">{error}</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="mt-4 w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>

        {/* Modal d'erreur */}
        <ErrorModalComponent />
      </div>
    );
  }

  // Vue détaillée d'une commission
  if (selectedCommission) {
    // Si un sous-dossier est sélectionné, afficher son contenu
    if (selectedFolder) {
      const { byFolder } = getOrganizedArchives();
      const folderArchives = byFolder[selectedFolder.id] || [];

      return (
        <div>
          <Header title="Archives" />

          <div className="px-4 md:px-8">
            {/* Breadcrumb */}
            <div className="mb-6">
              <button
                onClick={() => setSelectedFolder(null)}
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors group"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                <span>Retour à {selectedCommission.name}</span>
              </button>

              <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                <Folder size={16} />
                <ChevronRight size={16} />
                <span className="font-medium text-gray-700">{selectedCommission.name}</span>
                <ChevronRight size={16} />
                <span className="font-medium text-gray-900">{selectedFolder.name}</span>
              </div>
            </div>

            {/* En-tête du sous-dossier */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 md:p-8 mb-6 border border-blue-200">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center">
                    <WindowsFolder count={0} isHovered={false} size="normal" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      {selectedFolder.name}
                    </h2>
                    <p className="text-gray-700 font-medium mt-2">
                      {folderArchives.length} archive{folderArchives.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/documents/add?commissionId=${selectedCommission.id}`)}
                  className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all shadow-md hover:shadow-lg hover:scale-105"
                >
                  <Plus size={20} />
                  <span className="font-medium">Ajouter</span>
                </button>
              </div>
            </div>

            {/* Archives du sous-dossier (avec drag & drop pour retirer) */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Contenu du dossier
              </h3>
              {isDraggingMobile && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700 font-medium">
                    📤 Déposez ici pour retirer l'archive du dossier
                  </p>
                </div>
              )}
            </div>
            <div
              data-root-drop="true"
              onDragOver={handleDragOver}
              onDrop={handleDropOnRoot}
              className={`min-h-[200px] transition-all duration-300 ${
                isDraggingMobile
                  ? 'bg-orange-50/50 border-4 border-dashed border-orange-400 rounded-xl p-4 animate-pulse'
                  : ''
              }`}
            >
              {folderArchives.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {folderArchives.map((doc) => (
                    <div
                      key={doc.id}
                      draggable={canManageCommission(selectedCommission)}
                      onDragStart={canManageCommission(selectedCommission) ? (e) => handleDragStart(e, doc) : undefined}
                      onTouchStart={canManageCommission(selectedCommission) ? (e) => handleTouchStart(e, doc) : undefined}
                      onTouchMove={canManageCommission(selectedCommission) ? handleTouchMove : undefined}
                      onTouchEnd={canManageCommission(selectedCommission) ? handleTouchEnd : undefined}
                      style={canManageCommission(selectedCommission) ? { touchAction: 'none' } : {}}
                      className={`${canManageCommission(selectedCommission) ? 'cursor-move' : 'cursor-pointer'} transition-opacity select-none ${
                        isDraggingMobile && draggedArchive?.id === doc.id ? 'opacity-30' : ''
                      }`}
                    >
                      <DocumentCard document={doc} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <FileText className="mx-auto text-gray-300 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    Aucune archive dans ce dossier
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Glissez-déposez des archives ici ou ajoutez-en une nouvelle
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Modal d'erreur */}
          <ErrorModalComponent />
        </div>
      );
    }

    return (
      <div>
        <Header title="Archives" />

        <div className="px-4 md:px-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <button
              onClick={handleBackToFolders}
              className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors group"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              <span>Retour aux dossiers</span>
            </button>

            <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
              <Folder size={16} />
              <ChevronRight size={16} />
              <span className="font-medium text-gray-900">{selectedCommission.name}</span>
            </div>
          </div>

          {/* En-tête de la commission */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 md:p-8 mb-6 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center">
                  <WindowsFolder count={0} isHovered={false} size="normal" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {selectedCommission.name}
                  </h2>
                  {selectedCommission.description && (
                    <p className="text-gray-600 text-sm">
                      {selectedCommission.description}
                    </p>
                  )}
                  <p className="text-gray-700 font-medium mt-2">
                    {selectedCommission.count} archive{selectedCommission.count > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/documents/add?commissionId=${selectedCommission.id}`)}
                className="hidden md:flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all shadow-md hover:shadow-lg hover:scale-105"
              >
                <Plus size={20} />
                <span className="font-medium">Ajouter</span>
              </button>
            </div>
          </div>

          {/* Bouton pour créer un sous-dossier */}
          <div className="mb-6 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <button
              onClick={handleCreateFolderClick}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors font-medium shadow-sm"
            >
              <FolderPlus size={20} />
              <span>Créer un sous-dossier</span>
            </button>

            {/* Message pour les non-membres */}
            {!canManageCommission(selectedCommission) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs">
                <p className="text-amber-800 font-medium">
                  ℹ️ Vous pouvez consulter cette commission mais seuls les membres peuvent créer des dossiers et organiser les archives.
                </p>
              </div>
            )}

            {/* Aide mobile */}
            {canManageCommission(selectedCommission) && selectedCommission.archives.length > 0 && (
              <div className="md:hidden bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-3 text-xs">
                <p className="text-primary-800 font-medium">
                  💡 <strong>Astuce :</strong> Maintenez le doigt sur une archive et déplacez-la vers un dossier pour l'organiser
                </p>
              </div>
            )}
          </div>

          {/* Sous-dossiers */}
          {subFolders.length > 0 && (
            <div className="mb-8">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Sous-dossiers</h3>
                {isDraggingMobile && (
                  <p className="text-sm text-blue-600 font-medium mt-1 animate-pulse">
                    ⬇️ Déposez sur un dossier ci-dessous
                  </p>
                )}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                {subFolders.map((folder) => {
                  const { byFolder } = getOrganizedArchives();
                  const folderArchives = byFolder[folder.id] || [];

                  return (
                    <div
                      key={folder.id}
                      data-folder-id={folder.id}
                      onClick={() => !isDraggingMobile && handleFolderClick(folder)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDropOnFolder(e, folder.id)}
                      className={`group flex flex-col items-center text-center cursor-pointer transition-all duration-200 relative ${
                        isDraggingMobile
                          ? 'ring-4 ring-blue-400 ring-offset-2 rounded-xl animate-pulse scale-125 bg-blue-50/50 p-4 shadow-xl z-10'
                          : 'hover:scale-105'
                      }`}
                      style={isDraggingMobile ? { minWidth: '120px', minHeight: '120px' } : {}}
                    >
                      <div className="mb-2">
                        <WindowsFolder
                          count={viewedFolders.includes(folder.id) ? 0 : folderArchives.length}
                          isHovered={false}
                          size="small"
                        />
                      </div>
                      <h4 className="font-medium text-xs text-gray-900 line-clamp-2 px-1 group-hover:text-blue-600 transition-colors leading-tight">
                        {folder.name}
                      </h4>
                      <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-0.5">
                        <FileText size={10} />
                        <span>{folderArchives.length}</span>
                      </div>

                      {/* Bouton supprimer (visible au survol, uniquement pour les membres) */}
                      {canManageCommission(selectedCommission) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id);
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                          title="Supprimer le dossier"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Archives sans dossier (zone de dépôt pour retirer des dossiers) */}
          <div
            data-root-drop="true"
            onDragOver={handleDragOver}
            onDrop={handleDropOnRoot}
            className={`min-h-[200px] border-2 border-dashed rounded-xl p-4 bg-gray-50/50 transition-colors ${
              isDraggingMobile ? 'border-green-400 bg-green-50/50' : 'border-gray-300'
            }`}
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Archives non classées
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                <span className="hidden md:inline">Glissez-déposez pour organiser</span>
                <span className="md:hidden">Maintenez et déplacez pour organiser</span>
              </p>
              {isDraggingMobile && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700 font-medium">
                    📂 Déposez sur un dossier pour classer l'archive
                  </p>
                </div>
              )}
            </div>

            {selectedCommission.archives.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getOrganizedArchives().withoutFolder.map((doc) => (
                  <div
                    key={doc.id}
                    draggable={canManageCommission(selectedCommission)}
                    onDragStart={canManageCommission(selectedCommission) ? (e) => handleDragStart(e, doc) : undefined}
                    onTouchStart={canManageCommission(selectedCommission) ? (e) => handleTouchStart(e, doc) : undefined}
                    onTouchMove={canManageCommission(selectedCommission) ? handleTouchMove : undefined}
                    onTouchEnd={canManageCommission(selectedCommission) ? handleTouchEnd : undefined}
                    style={canManageCommission(selectedCommission) ? { touchAction: 'none' } : {}}
                    className={`${canManageCommission(selectedCommission) ? 'cursor-move' : 'cursor-pointer'} transition-opacity select-none ${
                      isDraggingMobile && draggedArchive?.id === doc.id ? 'opacity-30' : ''
                    }`}
                  >
                    <DocumentCard document={doc} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <FileText className="mx-auto text-gray-300 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aucune archive dans cette commission
                </h3>
                <p className="text-gray-500 mb-6">
                  Ajoutez votre première archive
                </p>
                <button
                  onClick={() => navigate(`/documents/add?commissionId=${selectedCommission.id}`)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm font-medium"
                >
                  <Plus size={20} />
                  <span>Ajouter une archive</span>
                </button>
              </div>
            )}
          </div>

          {/* Modal pour créer un sous-dossier */}
          {showCreateFolderModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Créer un sous-dossier</h3>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Nom du dossier"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleCreateFolder();
                  }}
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowCreateFolderModal(false);
                      setNewFolderName('');
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim()}
                    className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal d'erreur */}
          <ErrorModalComponent />
        </div>
      </div>
    );
  }

  // Vue principale (dossiers ou résultats de recherche)
  return (
    <div>
      <Header title="Archives" />

      <div className="px-4 md:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            {viewMode === 'search' ? (
              <p className="text-gray-600">
                {filteredArchives.length} archive{filteredArchives.length > 1 ? 's' : ''} trouvée
                {filteredArchives.length > 1 ? 's' : ''}
              </p>
            ) : (
              <p className="text-gray-600">
                {commissions.length} commission{commissions.length > 1 ? 's' : ''}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/documents/add')}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all shadow-md hover:shadow-lg hover:scale-105 font-medium"
          >
            <Plus size={20} />
            <span className="hidden md:inline">Ajouter une archive</span>
            <span className="md:hidden">Ajouter</span>
          </button>
        </div>

        <SearchBar
          onSearch={handleSearch}
          commissions={commissions}
          types={documentTypes}
          years={years}
        />

        {/* Vue dossiers */}
        {viewMode === 'folders' && (
          <>
            {commissionsWithArchives.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4 md:gap-6">
                {commissionsWithArchives.map((commission) => (
                  <div
                    key={commission.id}
                    onClick={() => handleCommissionClick(commission)}
                    onMouseEnter={() => setHoveredFolderId(commission.id)}
                    onMouseLeave={() => setHoveredFolderId(null)}
                    className="group flex flex-col items-center text-center cursor-pointer transition-all duration-200 hover:scale-105"
                  >
                    {/* Dossier Windows */}
                    <div className="mb-2">
                      <WindowsFolder
                        count={viewedCommissions.includes(commission.id) ? 0 : commission.count}
                        isHovered={hoveredFolderId === commission.id}
                        size="small"
                      />
                    </div>

                    {/* Nom de la commission */}
                    <h3 className="font-medium text-xs text-gray-900 line-clamp-2 px-1 group-hover:text-blue-600 transition-colors leading-tight">
                      {commission.name}
                    </h3>

                    {/* Nombre d'archives */}
                    <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-0.5">
                      <FileText size={10} />
                      <span>
                        {commission.count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="inline-block mb-4 opacity-30 scale-150">
                  <WindowsFolder count={0} isHovered={false} size="normal" />
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aucune commission disponible
                </h3>
                <p className="text-gray-500">
                  Les commissions apparaîtront ici une fois créées
                </p>
              </div>
            )}
          </>
        )}

        {/* Vue résultats de recherche */}
        {viewMode === 'search' && (
          <>
            {filteredArchives.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredArchives.map((doc) => (
                  <DocumentCard key={doc.id} document={doc} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <Search className="mx-auto text-gray-300 mb-4" size={64} />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aucune archive trouvée
                </h3>
                <p className="text-gray-500 mb-6">
                  Essayez de modifier vos critères de recherche
                </p>
                <button
                  onClick={handleBackToFolders}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  <ArrowLeft size={20} />
                  <span>Retour aux dossiers</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Feedback visuel pour le drag mobile */}
      {dragPreview.visible && draggedArchive && (
        <>
          {/* Carte flottante */}
          <div
            className="fixed pointer-events-none z-50 animate-bounce-subtle"
            style={{
              left: dragPreview.x - 60,
              top: dragPreview.y - 60,
              transform: 'scale(0.85) rotate(-5deg)',
            }}
          >
            <div className="bg-white rounded-xl shadow-2xl border-4 border-primary-500 p-4 w-28 h-28 flex flex-col items-center justify-center">
              <FileText className="text-primary-600 mb-2" size={36} />
              <p className="text-[11px] font-bold text-gray-800 line-clamp-2 text-center">
                {draggedArchive.title}
              </p>
            </div>
          </div>

          {/* Overlay semi-transparent pour indiquer le mode drag */}
          <div className="fixed inset-0 bg-black/10 z-40 pointer-events-none" />
        </>
      )}

      {/* Notification de succès */}
      {dropSuccess && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-green-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3">
            <CheckCircle size={24} />
            <span className="font-semibold">Archive organisée avec succès !</span>
          </div>
        </div>
      )}

      {/* Modal d'erreur */}
      <ErrorModalComponent />

      <style jsx>{`
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0) scale(0.85) rotate(-5deg); }
          50% { transform: translateY(-5px) scale(0.85) rotate(-5deg); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 0.6s ease-in-out infinite;
        }
        @keyframes slide-down {
          0% {
            transform: translate(-50%, -100%);
            opacity: 0;
          }
          100% {
            transform: translate(-50%, 0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DocumentsList;
